import { Droplets, PackagePlus, Pencil, Plus, ReceiptText, Save, Trash2, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, newIdempotencyKey } from "../api";
import { brl, invalidateApiData, useApiData } from "../hooks";
import { decimalUnits, workOrderNetAmount } from "../money";
import { mutateOffline } from "../offline-queue";
import type { Material, WorkOrder } from "../types";

type Tool = "material" | "cost" | "reservoir" | null;
type EditingRecord = { kind: Exclude<Tool, null>; id: string } | null;
type ReservoirDetail = NonNullable<WorkOrder["reservoirDetails"]>[number];

const inspectionOptions = [
  ["not_inspected", "Não inspecionado"],
  ["no", "Não"],
  ["yes", "Sim"],
] as const;

const isReservoirCleaning = (workOrder: WorkOrder) => workOrder.items?.some(({ serviceCode, name }) => {
  if (["WATER_TANK_CLEANING", "CISTERN_CLEANING"].includes(serviceCode ?? "")) return true;
  const normalized = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return /(caixa.?d.?agua|cisterna|reservatorio|limpeza.*agua)/.test(normalized);
}) ?? false;

const newReservoir = (): ReservoirDetail => ({
  id: crypto.randomUUID(),
  type: "water_tank",
  label: "",
  capacityLiters: 1000,
  material: "polyethylene",
  position: "elevated",
  coverCondition: "not_inspected",
  debris: "not_inspected",
  pests: "not_inspected",
  nearSewage: "not_inspected",
  cracks: "not_inspected",
  floatValve: "not_inspected",
  notes: "",
});

const costTypeLabel: Record<string, string> = {
  transport: "Transporte",
  fuel: "Combustível",
  toll: "Pedágio",
  parking: "Estacionamento",
  meal: "Alimentação",
  labor: "Mão de obra",
  third_party: "Terceiro",
  equipment: "Equipamento",
  other: "Outro",
};

const moneyFromTenThousandths = (value: bigint) => {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const cents = (absolute + 50n) / 100n;
  const decimal = `${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`;
  return negative ? `-${decimal}` : decimal;
};

export const workOrderCostSummary = (workOrder: WorkOrder) => {
  const active = (status: string) => !["rejected", "reversed"].includes(status);
  const materials = workOrder.materials?.filter(({ status }) => active(status)).reduce((total, item) => total + decimalUnits(item.totalCost, 4), 0n) ?? 0n;
  const reported = workOrder.costs?.filter(({ status }) => active(status)).reduce((total, item) => total + decimalUnits(item.amount, 4), 0n) ?? 0n;
  const total = materials + reported;
  const serviceValue = decimalUnits(workOrderNetAmount(workOrder), 4);
  const result = serviceValue - total;
  return {
    serviceValue: moneyFromTenThousandths(serviceValue),
    materials: moneyFromTenThousandths(materials),
    reported: moneyFromTenThousandths(reported),
    total: moneyFromTenThousandths(total),
    result: moneyFromTenThousandths(result),
  };
};

const quantity = (raw: string) => {
  const clean = raw.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{0,3})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  return `${whole}.${fraction.padEnd(3, "0")}`;
};

const unitCost = (raw: string) => {
  const clean = raw.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{0,4})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  return `${whole}.${fraction.padEnd(4, "0")}`;
};

export const unitCostInput = (raw: string) => {
  const [whole, fraction = ""] = raw.trim().split(".");
  const meaningfulFraction = fraction.replace(/0+$/, "");
  return `${whole},${meaningfulFraction.padEnd(2, "0")}`;
};

export const canCorrectFieldRecord = (kind: "cost" | "material", status: string) => kind === "cost"
  ? ["reported", "pending_approval", "paid"].includes(status)
  : status === "pending";

const pluralUnit: Record<string, string> = {
  serviço: "serviços",
  unidade: "unidades",
  hora: "horas",
  dia: "dias",
  litro: "litros",
};
const serviceQuantity = (raw: string, unit: string) => {
  const amount = Number(raw);
  const displayUnit = amount === 1 ? unit : pluralUnit[unit.toLowerCase()] ?? unit;
  return `${amount.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} ${displayUnit}`;
};

const money = (raw: string) => {
  const clean = raw.trim().replace(",", ".");
  if (!/^\d+(?:\.\d{0,2})?$/.test(clean)) return null;
  const [whole, fraction = ""] = clean.split(".");
  return `${whole}.${fraction.padEnd(2, "0")}`;
};

const invalidateCostViews = () => {
  invalidateApiData("/finance/summary");
  invalidateApiData("/finance/movements");
  invalidateApiData("/dashboard/summary");
};

export function FieldTools({ workOrder, reload, notify }: {
  workOrder: WorkOrder;
  reload: () => Promise<void>;
  notify: (value: string) => void;
}) {
  const [tool, setTool] = useState<Tool>(null);
  const [editing, setEditing] = useState<EditingRecord>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const materials = useApiData<Material[]>(tool === "material" ? "/materials" : null);
  const [materialId, setMaterialId] = useState("");
  const [materialQuantity, setMaterialQuantity] = useState("");
  const [cost, setCost] = useState("");
  const [fieldCostType, setFieldCostType] = useState("transport");
  const [fieldCostDescription, setFieldCostDescription] = useState("");
  const [fieldCostAmount, setFieldCostAmount] = useState("");
  const [reservoirs, setReservoirs] = useState<ReservoirDetail[]>([]);
  const [hideServiceValue, setHideServiceValue] = useState(false);
  const selectedMaterial = materials.data?.find(({ id }) => id === materialId);
  const services = workOrder.items ?? [];
  const records = [
    ...(workOrder.costs ?? []).map((item) => ({ id: item.id, kind: "cost" as const, category: costTypeLabel[item.costType] ?? "Outro", label: item.description, amount: item.amount, editable: canCorrectFieldRecord("cost", item.status) })),
    ...(workOrder.materials ?? []).map((item) => ({ id: item.id, kind: "material" as const, category: "Material", label: item.name, amount: item.totalCost, editable: canCorrectFieldRecord("material", item.status) })),
  ];

  const close = () => { setTool(null); setEditing(null); setError(""); };

  const startNewCost = () => {
    setEditing(null); setTool("cost"); setError("");
    setFieldCostType("transport"); setFieldCostDescription(""); setFieldCostAmount("");
  };

  const startNewMaterial = () => {
    setEditing(null); setTool("material"); setError("");
    setMaterialId(""); setMaterialQuantity(""); setCost("");
  };

  const startReservoirs = () => {
    setEditing(null); setTool("reservoir"); setError("");
    setReservoirs(workOrder.reservoirDetails?.map((item) => ({ ...item })) ?? [newReservoir()]);
    setHideServiceValue(Boolean(workOrder.hideServiceValue));
  };

  const updateReservoir = <K extends keyof ReservoirDetail>(index: number, key: K, value: ReservoirDetail[K]) => {
    setReservoirs((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };

  const saveReservoirs = async (event: FormEvent) => {
    event.preventDefault();
    if (reservoirs.some(({ label, capacityLiters }) => label.trim().length < 2 || capacityLiters <= 0)) {
      return setError("Revise a identificação e a capacidade dos reservatórios.");
    }
    setBusy(true); setError("");
    try {
      await api(`/work-orders/${workOrder.id}/reservoirs`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey(), "If-Match": `"${workOrder.version}"` },
        body: JSON.stringify({ reservoirs: reservoirs.map((item) => ({ ...item, label: item.label.trim(), notes: item.notes?.trim() || undefined })), hideServiceValue }),
      });
      notify("Reservatórios atualizados.");
      invalidateApiData(`/work-orders/${workOrder.id}/documents`);
      await reload();
      close();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Reservatórios não atualizados"); }
    finally { setBusy(false); }
  };

  const editCost = (id: string) => {
    const item = workOrder.costs?.find((costItem) => costItem.id === id);
    if (!item) return;
    setEditing({ kind: "cost", id }); setTool("cost"); setError("");
    setFieldCostType(item.costType); setFieldCostDescription(item.description); setFieldCostAmount(item.amount.replace(".", ","));
  };

  const editMaterial = (id: string) => {
    const item = workOrder.materials?.find((materialItem) => materialItem.id === id);
    if (!item) return;
    setEditing({ kind: "material", id }); setTool("material"); setError("");
    setMaterialId(item.materialId); setMaterialQuantity(item.quantity.replace(".", ",")); setCost(unitCostInput(item.unitCost));
  };

  const saveMaterial = async (event: FormEvent) => {
    event.preventDefault();
    const exactQuantity = quantity(materialQuantity);
    const exactCost = unitCost(cost || selectedMaterial?.standardCost || "0");
    if (!selectedMaterial || !exactQuantity || !exactCost) return setError("Revise material, quantidade e custo.");
    setBusy(true); setError("");
    try {
      const payload = { materialId, quantity: exactQuantity, unit: selectedMaterial.unit, unitCost: exactCost };
      if (editing?.kind === "material") {
        await api(`/work-orders/${workOrder.id}/materials/${editing.id}/corrections`, {
          method: "POST", headers: { "Idempotency-Key": newIdempotencyKey() }, body: JSON.stringify(payload),
        });
        notify("Material corrigido."); await reload(); invalidateCostViews();
      } else {
        const result = await mutateOffline<{ id: string }>(`/work-orders/${workOrder.id}/materials`, payload);
        notify("queued" in result ? "Material salvo para envio." : "Material registrado.");
        if (!("queued" in result)) { await reload(); invalidateCostViews(); }
      }
      setMaterialId(""); setMaterialQuantity(""); setCost(""); close();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Material não registrado"); }
    finally { setBusy(false); }
  };

  const saveFieldCost = async (event: FormEvent) => {
    event.preventDefault();
    const amount = money(fieldCostAmount);
    if (!amount || !fieldCostDescription.trim()) return setError("Revise a descrição e o valor.");
    setBusy(true); setError("");
    try {
      const previous = editing?.kind === "cost" ? workOrder.costs?.find(({ id }) => id === editing.id) : undefined;
      const incurredAt = previous ? `${previous.competenceDate}T12:00:00-03:00` : new Date().toISOString();
      const payload = { costType: fieldCostType, description: fieldCostDescription.trim(), amount, incurredAt };
      if (editing?.kind === "cost") {
        await api(`/work-orders/${workOrder.id}/costs/${editing.id}/corrections`, {
          method: "POST", headers: { "Idempotency-Key": newIdempotencyKey() }, body: JSON.stringify(payload),
        });
        notify("Custo corrigido."); await reload(); invalidateCostViews();
      } else {
        const result = await mutateOffline<{ id: string }>(`/work-orders/${workOrder.id}/costs`, payload);
        notify("queued" in result ? "Custo salvo para envio." : "Custo registrado.");
        if (!("queued" in result)) { await reload(); invalidateCostViews(); }
      }
      setFieldCostDescription(""); setFieldCostAmount(""); close();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Custo não registrado"); }
    finally { setBusy(false); }
  };

  return <section className="field-tools work-order-cost-card">
    <div className="work-order-service-tools">
      <div className="work-order-service-summary">
        <span>{services.length === 1 ? "Serviço" : "Serviços"}</span>
        <div>{services.length > 0
          ? services.map((service, index) => <span className="work-order-service-item" key={`${service.serviceTypeId}-${index}`}><strong>{service.name}</strong><small>{serviceQuantity(service.quantity, service.unit)}</small></span>)
          : <strong>Não informado</strong>}</div>
      </div>
      <div className="field-tool-grid field-tool-grid-simple work-order-cost-actions">
        <button type="button" onClick={startNewCost}><ReceiptText /><span className="work-order-tool-label-desktop">Registrar custo</span><span className="work-order-tool-label-mobile">Custo</span></button>
        <button type="button" onClick={startNewMaterial}><PackagePlus /><span className="work-order-tool-label-desktop">Adicionar material</span><span className="work-order-tool-label-mobile">Material</span></button>
        {isReservoirCleaning(workOrder) && <button type="button" onClick={startReservoirs}><Droplets /><span className="work-order-tool-label-desktop">Reservatórios</span><span className="work-order-tool-label-mobile">Reservat.</span></button>}
      </div>
    </div>
    {tool && <div className="field-tool-panel">
      <header><strong>{tool === "material" ? "Material utilizado" : tool === "reservoir" ? "Reservatórios atendidos" : "Registrar custo"}</strong><button type="button" onClick={close} aria-label="Fechar"><X /></button></header>
      {tool === "material" && <form className="tool-form" onSubmit={saveMaterial}><label>Material<select value={materialId} onChange={(event) => { const next = materials.data?.find(({ id }) => id === event.target.value); setMaterialId(event.target.value); setCost(next?.standardCost?.replace(".", ",") ?? ""); }} required><option value="">Selecione</option>{materials.data?.map((material) => <option key={material.id} value={material.id}>{material.name}</option>)}</select></label><div className="tool-form-row"><label>Quantidade<input inputMode="decimal" value={materialQuantity} onChange={(event) => setMaterialQuantity(event.target.value)} required /></label><label>Custo unitário<input inputMode="decimal" value={cost} onChange={(event) => setCost(event.target.value)} required /></label></div><button className="site-button site-button-navy" disabled={busy}><Save size={16} />{editing ? "Salvar correção" : "Salvar material"}</button></form>}
      {tool === "cost" && <form className="tool-form" onSubmit={saveFieldCost}><label>Tipo<select value={fieldCostType} onChange={(event) => setFieldCostType(event.target.value)}><option value="transport">Transporte</option><option value="fuel">Combustível</option><option value="toll">Pedágio</option><option value="parking">Estacionamento</option><option value="meal">Alimentação</option><option value="labor">Mão de obra</option><option value="third_party">Terceiro</option><option value="equipment">Equipamento</option><option value="other">Outro</option></select></label><label>Descrição<input value={fieldCostDescription} onChange={(event) => setFieldCostDescription(event.target.value)} required minLength={3} maxLength={500} /></label><label>Valor<input inputMode="decimal" value={fieldCostAmount} onChange={(event) => setFieldCostAmount(event.target.value)} placeholder="0,00" required /></label><button className="site-button site-button-navy" disabled={busy}><Save size={16} />{editing ? "Salvar correção" : "Salvar custo"}</button></form>}
      {tool === "reservoir" && <form className="tool-form reservoir-form" onSubmit={saveReservoirs}>
        <div className="reservoir-editor-list">{reservoirs.map((reservoir, index) => <fieldset className="reservoir-editor" key={reservoir.id}>
          <legend className="visually-hidden">Reservatório {index + 1}</legend>
          <div className="reservoir-editor-header"><strong>Reservatório {index + 1}</strong><button type="button" onClick={() => setReservoirs((current) => current.filter(({ id }) => id !== reservoir.id))} aria-label={`Remover ${reservoir.label}`}><Trash2 size={15} /></button></div>
          <div className="reservoir-primary-fields">
            <label>Tipo<select value={reservoir.type} onChange={(event) => {
              const type = event.target.value as ReservoirDetail["type"];
              updateReservoir(index, "type", type);
            }}><option value="water_tank">Caixa-d'água</option><option value="cistern">Cisterna</option></select></label>
            <label>Identificação<input value={reservoir.label} onChange={(event) => updateReservoir(index, "label", event.target.value)} placeholder={reservoir.type === "cistern" ? "Ex.: Cisterna principal" : "Ex.: Caixa-d'água superior"} required maxLength={80} /></label>
            <label>Capacidade (litros)<input type="number" min="1" max="10000000" step="1" value={reservoir.capacityLiters} onChange={(event) => updateReservoir(index, "capacityLiters", Number(event.target.value))} required /></label>
            <label>Material<select value={reservoir.material} onChange={(event) => updateReservoir(index, "material", event.target.value)}><option value="concrete">Concreto</option><option value="masonry">Alvenaria</option><option value="fiberglass">Fibra de vidro</option><option value="polyethylene">Polietileno</option><option value="fiber_cement">Fibrocimento / amianto</option><option value="stainless_steel">Aço inoxidável</option><option value="other">Outro</option></select></label>
            <label>Posição<select value={reservoir.position} onChange={(event) => updateReservoir(index, "position", event.target.value)}><option value="elevated">Elevada</option><option value="supported">Apoiada</option><option value="buried">Enterrada</option><option value="semi_buried">Semi-enterrada</option></select></label>
            <label>Cobertura<select value={reservoir.coverCondition} onChange={(event) => updateReservoir(index, "coverCondition", event.target.value)}><option value="not_inspected">Não inspecionada</option><option value="fully_covered">Totalmente coberta</option><option value="partially_covered">Parcialmente coberta</option><option value="uncovered">Sem cobertura</option></select></label>
          </div>
          <div className="reservoir-inspection-fields">
            {([
              ["debris", "Detritos"],
              ["pests", "Vetores ou animais"],
              ["nearSewage", "Próxima de esgoto ou fossa"],
              ["cracks", "Fendas ou rachaduras"],
              ["floatValve", "Torneira de boia"],
            ] as const).map(([key, label]) => <label key={key}>{label}<select value={reservoir[key]} onChange={(event) => updateReservoir(index, key, event.target.value)}>{inspectionOptions.map(([value, text]) => <option value={value} key={value}>{text}</option>)}</select></label>)}
          </div>
          <label>Observações<textarea value={reservoir.notes ?? ""} onChange={(event) => updateReservoir(index, "notes", event.target.value)} maxLength={1000} /></label>
        </fieldset>)}</div>
        <label className="check-field"><input type="checkbox" checked={hideServiceValue} onChange={(event) => setHideServiceValue(event.target.checked)} /> Ocultar valor do serviço na OS</label>
        <div className="reservoir-form-actions"><button className="site-button site-button-outline" type="button" onClick={() => setReservoirs((current) => [...current, newReservoir()])}><Plus size={16} /> Adicionar reservatório</button><button className="site-button site-button-navy" disabled={busy}><Save size={16} /> {busy ? "Salvando…" : "Salvar reservatórios"}</button></div>
      </form>}
      {error && <div className="form-error" role="alert">{error}</div>}
    </div>}
    {records.length > 0 && <div className="work-order-cost-records" aria-label="Custos registrados">{records.map((item) => <div key={`${item.kind}-${item.id}`}><p><span>{item.category}</span>{item.label}</p><div className="work-order-cost-record-value"><strong>{brl(item.amount)}</strong>{item.editable && <button type="button" onClick={() => item.kind === "cost" ? editCost(item.id) : editMaterial(item.id)} aria-label={`Corrigir ${item.kind === "cost" ? "custo" : "material"}: ${item.label}`}><Pencil size={14} /></button>}</div></div>)}</div>}
    {(workOrder.reservoirDetails?.length ?? 0) > 0 && <div className="reservoir-summary-list" aria-label="Reservatórios atendidos">{workOrder.reservoirDetails!.map((reservoir) => <article key={reservoir.id}><Droplets /><span><strong>{reservoir.label} · {new Intl.NumberFormat("pt-BR").format(reservoir.capacityLiters)} L</strong><small>{({ concrete: "Concreto", masonry: "Alvenaria", fiberglass: "Fibra de vidro", polyethylene: "Polietileno", fiber_cement: "Fibrocimento / amianto", stainless_steel: "Aço inoxidável", other: "Outro" } as Record<string, string>)[reservoir.material]} · {({ elevated: "Elevada", supported: "Apoiada", buried: "Enterrada", semi_buried: "Semi-enterrada" } as Record<string, string>)[reservoir.position]}</small></span></article>)}</div>}
  </section>;
}
